// Display-formatting helpers shared across the BMD report tabs.

/**
 * Curriculum names arrive HTML-encoded from the database and must go through
 * this before being rendered, or the tutor reads a literal
 * "Use &lt;, &gt; and = to compare numbers up to 100".
 *
 * Only a handful of rows are affected, but they are exactly the skills whose
 * names need those characters: inequalities and comparisons.
 *
 * Decoding through the DOM also strips stray markup, which is what a label
 * wants; dangerouslySetInnerHTML would render database content as live HTML.
 * Falls back to the raw string when there is no DOM.
 */
export function plainText(value: string | null | undefined): string {
  if (!value) return ""
  if (typeof document === "undefined") return String(value)
  const tmp = document.createElement("div")
  tmp.innerHTML = String(value)
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim()
}

/**
 * "Year 4" to "Yr 4". Returns "—" for missing input. For badges and chain
 * rows, where the full label is too wide.
 */
export function abbreviateYearLevel(name: string | null | undefined): string {
  if (!name) return "—"
  return name.replace(/^Year\s+/i, "Yr ").trim()
}

/**
 * "Year 4" or "Yr 4" to 4. Returns null when the input has no digits. Callers
 * that need a default should coalesce explicitly, so a missing year does not
 * silently sort as 0.
 */
export function parseYearNumber(
  name: string | null | undefined,
): number | null {
  if (!name) return null
  const m = String(name).match(/(\d+)/)
  return m ? Number(m[1]) : null
}

/**
 * misconception_type enum to a tutor-readable label. Null and unrecognized
 * values become "Untagged", which is what an answer LCMS has not classified
 * yet comes through as.
 */
export function humanMisconception(m: string | null | undefined): string {
  if (!m) return "Untagged"
  switch (m) {
    case "FUNDAMENTAL":
      return "Fundamental"
    case "RECENTLY_LEARNED":
      return "Recently Learned"
    case "LACK_OF_DEPTH":
      return "Lack of Depth"
    case "SKIP_ONLY":
      return "Skipped"
    default:
      return m
  }
}
